import type { TFunction } from "i18next";
import type { Step } from "react-joyride";

import { TOUR_HELP_TARGET } from "./constants";
import type { TourDefinition, TourRunnerStep } from "./types";

const FINISH_STEP_ID = "finish";

const STAY_IN_VIEWPORT = {
  flipOptions: { boundary: [] },
  shiftOptions: { crossAxis: true, boundary: [] },
};

type BuildTourStepsParams = {
  definition: TourDefinition;
  stepIds: string[];
  t: TFunction;
  getTarget: (name: string) => HTMLElement | null;
  getContent: (id: string) => Step["content"];
};

export function buildTourSteps({ definition, stepIds, t, getTarget, getContent }: BuildTourStepsParams): TourRunnerStep[] {
  const key = (part: string) => `${definition.copyKey}.${part}`;

  const stepSteps = definition.steps
    .filter((step) => stepIds.includes(step.id))
    .map<TourRunnerStep>((step) => ({
      id: step.id,
      target: () => getTarget(step.target),
      title: t(key(`step.${step.id}.title`)),
      content: getContent(step.id),
      placement: step.placement,
      skipScroll: true,
      floatingOptions: {
        ...STAY_IN_VIEWPORT,
        ...(step.isMovingTarget && { autoUpdate: { animationFrame: true } }),
      },
      ...(step.isFixed && { isFixed: true }),
    }));

  const finishStep: TourRunnerStep = {
    id: FINISH_STEP_ID,
    target: () => getTarget(TOUR_HELP_TARGET),
    title: t(key("finish.title")),
    content: t(key("finish.body")),
    placement: definition.finishPlacement,
    skipScroll: true,
    floatingOptions: STAY_IN_VIEWPORT,
  };

  return [...stepSteps, finishStep];
}
